/**
 * Order of the rendered entries in one Kanban column (one droppable).
 *
 * Dragging: the active card stays mounted so dnd-kit keeps its node. Own slot
 * targeted → it is shown faded in place; otherwise it is collapsed, takes no
 * slot, and a placeholder sits at the preview index.
 * Settling: the dropped card is pulled out and shown at its drop index until
 * items catch up.
 */

import type {
  KanbanDropPreview,
  KanbanItemBase,
} from './Kanban.config.types';
import type { KanbanSettlingDrop } from './useKanbanDropSettle';

export interface KanbanColumnCardSlot<TItem extends KanbanItemBase> {
  kind: 'card' | 'placeholder';
  item: TItem;
  /** The active card itself (collapsed or faded in place). */
  isDragSource: boolean;
}

export interface KanbanColumnCardsLayout<TItem extends KanbanItemBase> {
  slots: KanbanColumnCardSlot<TItem>[];
  /** Active card is in this column but another slot is targeted. */
  isSourceCollapsed: boolean;
}

export function layoutKanbanColumnCards<TItem extends KanbanItemBase>({
  droppableId,
  columnItems,
  activeItem,
  dropPreview,
  settling,
  settlingItem,
}: {
  droppableId: string;
  columnItems: TItem[];
  activeItem: TItem | null;
  dropPreview: KanbanDropPreview | null;
  settling: KanbanSettlingDrop | null;
  settlingItem: TItem | null;
}): KanbanColumnCardsLayout<TItem> {
  const activeIndex = activeItem
    ? columnItems.findIndex((entry) => entry.id === activeItem.id)
    : -1;
  const isSourceInPlace =
    activeIndex >= 0 &&
    dropPreview?.droppableId === droppableId &&
    dropPreview.index === activeIndex;
  const isSourceCollapsed =
    activeIndex >= 0 && dropPreview != null && !isSourceInPlace;

  let inserted: KanbanColumnCardSlot<TItem> | null = null;
  let insertIndex = -1;
  if (
    activeItem &&
    !isSourceInPlace &&
    dropPreview?.droppableId === droppableId
  ) {
    inserted = { kind: 'placeholder', item: activeItem, isDragSource: false };
    insertIndex = dropPreview.index;
  } else if (
    !activeItem &&
    settlingItem &&
    settling?.preview.droppableId === droppableId
  ) {
    inserted = { kind: 'card', item: settlingItem, isDragSource: false };
    insertIndex = settling.preview.index;
  }

  const skippedId = activeItem?.id ?? settling?.itemId ?? null;
  const slots: KanbanColumnCardSlot<TItem>[] = [];
  let slot = 0;
  for (const item of columnItems) {
    if (item.id === skippedId) {
      if (activeItem) slots.push({ kind: 'card', item, isDragSource: true });
      continue;
    }
    if (inserted && insertIndex === slot) slots.push(inserted);
    slots.push({ kind: 'card', item, isDragSource: false });
    slot += 1;
  }
  if (inserted && insertIndex >= slot) slots.push(inserted);

  return { slots, isSourceCollapsed };
}
